import React, { useMemo, useState } from 'react';
import { IconLock as Lock, IconAlertTriangle as AlertTriangle, IconLayoutAlignMiddle as AlignCenterVertical, IconLayoutAlignBottom as AlignEndVertical, IconLayoutAlignTop as AlignStartVertical, IconArmchair as Armchair, IconArrowDown as ArrowDown, IconArrowLeft as ArrowLeft, IconArrowsLeftRight as ArrowLeftRight, IconArrowRight as ArrowRight, IconArrowUp as ArrowUp, IconCopy as Copy, IconWalk as Footprints, IconPlus as Plus, IconRotateClockwise2 as RotateCcw, IconRotateClockwise as RotateCw, IconVectorSpline as Spline, IconAlignCenter as TextAlignCenter, IconAlignRight as TextAlignEnd, IconAlignLeft as TextAlignStart, IconTrash as Trash2 } from '@tabler/icons-react';
import {
    getShape, LABEL_SCHEMES, measureRow, normalizeAngle, seatAbs, seatKey,
} from './geometry';
import AddPanel, { type CreateRequest } from './AddPanel';
import { IconPicker } from './objectIcons';
import {
    DEFAULT_SECTION_LABELS, defaultStartFor, fullZoneIndexes, regenSection, zoneSeatKeys, type SectionPatch,
} from './sections';
import {
    alignRows, fullRowIds, mapRows, planStats, removeCategory, renameCategory, resetTiering, setCategoryBasePrice, renumberRows, renumberSeats, reshapeRow,
    reshapeTable, reverseSeatOrder, tableShapeOf, rowGapInfo, setAccessible, setSeatCategory, spaceRows, updateRow, updateSeat, updateShape,
} from './planOps';
import type { EditorSettings, LabelScheme, Plan, PlanIndex, RowLabelSide, SectionAlign, Selection, Shape, Stage, TableShape, Tool } from './types';
import { useDialogs } from './dialogs';
import { Button, Choice, ColorInput, cx, Field, NumberInput, Section, SelectInput, TextInput, Toggle } from './ui';

const TABLE_SHAPES: { value: TableShape; label: string }[] = [
    { value: 'round', label: 'Round' },
    { value: 'rect', label: 'Square' },
    { value: 'triangle', label: 'Triangle' },
];

const ROW_LABEL_SIDES: { value: RowLabelSide; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'none', label: 'None' },
];

const SliderField: React.FC<{
    label: string;
    value: number;
    min: number;
    max: number;
    hint?: string;
    onChange: (v: number, live: boolean) => void;
}> = ({ label, value, min, max, hint, onChange }) => (
    <Field label={label} hint={hint}>
        <input
            type="range"
            min={min}
            max={max}
            step={1}
            value={Math.round(value)}
            onChange={e => onChange(Number(e.target.value), true)}
            className="flex-1 min-w-0 accent-primary40 dark:accent-nightAccent"
        />
        <div className="w-14 shrink-0">
            <NumberInput value={Math.round(value)} min={min} max={max} onCommit={v => onChange(v, false)} />
        </div>
    </Field>
);

const PALETTE = ['#3B82F6', '#F59E0B', '#8B5CF6', '#10B981', '#EF4444', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'];

type Props = {
    plan: Plan;
    index: PlanIndex;
    selection: Selection;
    settings: EditorSettings;
    tool: Tool;
    onSettings: (patch: Partial<EditorSettings>) => void;
    commit: (updater: (plan: Plan) => Plan, mergeKey?: string) => void;
    onSelect: (sel: Selection) => void;
    onKeepSelection: (sel: Selection) => void;
    onDelete: () => void;
    onDuplicate: () => void;
    onRotate: (deg: number, mergeKey?: string) => void;
    onCreate: (req: CreateRequest) => void;
    className?: string;
    lockedMessage?: string;
};

const CategoryPicker: React.FC<{ plan: Plan; current: string | null; onPick: (name: string) => void; disabled?: boolean }> = ({ plan, current, onPick, disabled }) => (
    <div className="flex flex-wrap gap-1.5">
        {plan.categories.map((c, i) => (
            <button
                key={c.name}
                type="button"
                title={i < 9 ? `Shortcut: ${i + 1}` : undefined}
                disabled={disabled}
                onClick={() => onPick(c.name)}
                className={cx(
                    'flex items-center gap-1.5 h-7 pl-1.5 pr-2.5 rounded-full border text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                    current === c.name
                        ? 'border-gray-900 bg-gray-900 text-white dark:border-nightAccent dark:bg-nightAccent dark:text-nightBg'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400 dark:border-nightBorder dark:text-nightTextSecondary',
                )}
            >
                <span className="h-4 w-4 rounded-full ring-1 ring-black/10" style={{ background: c.color }} />
                {c.name}
            </button>
        ))}
        <button
            type="button"
            title="Shortcut: 0"
            disabled={disabled}
            onClick={() => onPick('')}
            className={cx(
                'h-7 px-2.5 rounded-full border text-xs disabled:cursor-not-allowed disabled:opacity-60',
                current === '' ? 'border-gray-900 bg-gray-900 text-white dark:border-nightAccent dark:bg-nightAccent dark:text-nightBg' : 'border-dashed border-gray-300 text-gray-500 dark:border-nightBorder dark:text-nightMuted',
            )}
        >
            None
        </button>
    </div>
);

const NumberingTool: React.FC<{
    title: string;
    defaultScheme: LabelScheme;
    directions: [string, string];
    onApply: (start: number, scheme: LabelScheme, reverse: boolean) => void;
}> = ({ title, defaultScheme, directions, onApply }) => {
    const [start, setStart] = useState(1);
    const [scheme, setScheme] = useState<LabelScheme>(defaultScheme);
    const [reverse, setReverse] = useState(false);
    return (
        <div className="rounded border border-gray-200 p-2 space-y-2 dark:border-nightBorder">
            <div className="text-[11px] font-medium text-gray-700 dark:text-nightTextSecondary">{title}</div>
            <div className="grid grid-cols-2 gap-1.5">
                <SelectInput value={scheme} options={LABEL_SCHEMES} onChange={setScheme} />
                <NumberInput value={start} min={1} onCommit={v => setStart(Math.round(v))} placeholder="Start" />
            </div>
            <div className="flex gap-1.5">
                <SelectInput
                    value={reverse ? 'r' : 'f'}
                    options={[{ value: 'f', label: directions[0] }, { value: 'r', label: directions[1] }]}
                    onChange={v => setReverse(v === 'r')}
                />
                <Button variant="primary" onClick={() => onApply(start, scheme, reverse)}>Apply</Button>
            </div>
        </div>
    );
};

const PlanInspector: React.FC<Pick<Props, 'plan' | 'settings' | 'onSettings' | 'commit' | 'lockedMessage'>> = ({ plan, settings, onSettings, commit, lockedMessage }) => {
    const dialogs = useDialogs();
    const locked = !!lockedMessage;
    const stats = useMemo(() => planStats(plan), [plan]);
    const addCategory = () =>
        commit(p => {
            let n = p.categories.length + 1;
            while (p.categories.some(c => c.name === `Tier ${n}`)) n++;
            const color = PALETTE.find(c => !p.categories.some(x => x.color.toLowerCase() === c.toLowerCase())) ?? PALETTE[n % PALETTE.length];
            return { ...p, categories: [...p.categories, { name: `Tier ${n}`, color }] };
        });

    return (
        <>
            <Section title="Seat map">
                <Field label="Name">
                    <TextInput value={plan.name ?? ''} onCommit={v => commit(p => ({ ...p, name: v }))} />
                </Field>
                <div className="grid grid-cols-3 gap-2 pt-1">
                    {[
                        ['Seats', stats.seats],
                        ['Rows', stats.rows],
                        ['Zones', plan.zones.length],
                    ].map(([label, value]) => (
                        <div key={label} className="rounded bg-gray-50 px-2 py-1.5 dark:bg-nightElevated">
                            <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-nightTextSecondary">{value}</div>
                            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-nightMuted">{label}</div>
                        </div>
                    ))}
                </div>
                {stats.uncategorized > 0 && (
                    <p className="flex items-start gap-1.5 text-[11px] text-warning30 dark:text-warning60">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
                        {stats.uncategorized} seat{stats.uncategorized === 1 ? ' has' : 's have'} no tier
                    </p>
                )}
                {stats.duplicateSeats.length > 0 && (
                    <p className="flex items-start gap-1.5 text-[11px] text-warning30 dark:text-warning60">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
                        Duplicate seat numbers in {stats.duplicateSeats.slice(0, 3).join(', ')}{stats.duplicateSeats.length > 3 ? ` and ${stats.duplicateSeats.length - 3} more` : ''}
                    </p>
                )}
                {stats.accessible > 0 && (
                    <p className="text-[11px] text-gray-500 dark:text-nightMuted">{stats.accessible} wheelchair accessible seat{stats.accessible === 1 ? '' : 's'}</p>
                )}
            </Section>

            <Section
                title="Tiers"
                actions={
                    <div className="flex items-center gap-1.5">
                        {locked && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
                                <Lock className="h-3 w-3" /> Editing locked
                            </span>
                        )}
                    <button type="button" onClick={addCategory} disabled={locked} title={locked ? 'Editing is locked' : 'Add tier'} className="h-6 w-6 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-nightMuted dark:hover:bg-nightElevated">
                        <Plus className="h-3.5 w-3.5" />
                    </button>
                    </div>
                }
            >
                {locked && <p className="rounded bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">{lockedMessage}</p>}
                {plan.categories.length === 0 && <p className="text-[11px] text-gray-400">Add tiers to price seats differently.</p>}
                {plan.categories.map((c, i) => (
                    <div key={`${i}-${c.name}`} className="space-y-1">
                    <div className="group flex items-center gap-1.5">
                        <ColorInput
                            disabled={locked}
                            value={c.color}
                            onChange={color => commit(p => ({ ...p, categories: p.categories.map((x, j) => (j === i ? { ...x, color } : x)) }), `cat-color-${i}`)}
                        />
                        <TextInput
                            disabled={locked}
                            value={c.name}
                            onCommit={name => {
                                const next = name.trim();
                                if (!next || next === c.name) return;
                                if (plan.categories.some(x => x.name === next)) {
                                    dialogs.alert(`A tier named "${next}" already exists.`);
                                    return;
                                }
                                commit(p => renameCategory(p, c.name, next));
                            }}
                        />
                        <span className="w-8 text-right text-[10px] tabular-nums text-gray-400">{stats.byCategory.get(c.name) ?? 0}</span>
                        <button
                            type="button"
                            title="Delete tier"
                            disabled={locked}
                            onClick={() => commit(p => removeCategory(p, c.name))}
                            className="h-6 w-6 shrink-0 flex items-center justify-center rounded text-gray-400 opacity-0 group-hover:opacity-100 hover:text-error40 disabled:cursor-not-allowed disabled:hover:text-gray-400"
                        >
                            <Trash2 className="h-3 w-3" />
                        </button>
                    </div>
                    <Field label="Base price">
                        <TextInput
                            disabled={locked}
                            value={c.basePrice === undefined ? '' : String(c.basePrice)}
                            placeholder="Not set"
                            onCommit={raw => {
                                const text = raw.trim();
                                if (text === '') {
                                    commit(p => setCategoryBasePrice(p, c.name, undefined));
                                    return;
                                }
                                const value = Number(text);
                                if (!Number.isFinite(value) || value < 0) {
                                    dialogs.alert('Enter a base price of 0 or more, or leave it empty.');
                                    return;
                                }
                                commit(p => setCategoryBasePrice(p, c.name, value));
                            }}
                        />
                    </Field>
                    </div>
                ))}
                {(plan.categories.length > 0 || stats.byCategory.size > 0) && (
                    <button
                        type="button"
                        disabled={locked}
                        onClick={async () => {
                            const ok = await dialogs.confirm({
                                title: 'Reset to non-tiering',
                                message: 'Reset this venue to non-tiering? Tier assignments and tier-specific pricing configuration will be removed from this venue. This will not affect the original shared venue. Existing ticket sales may limit which changes are allowed.',
                                confirmLabel: 'Reset',
                                destructive: true,
                            });
                            if (ok) commit(p => resetTiering(p));
                        }}
                        className="mt-1 w-full h-8 rounded border border-error80 text-xs font-semibold text-error40 hover:bg-error95 disabled:cursor-not-allowed disabled:opacity-50 dark:border-error30 dark:hover:bg-error10"
                    >
                        Reset to non-tiering
                    </button>
                )}
            </Section>

            <Section title="New rows">
                <Field label="Seat spacing">
                    <NumberInput value={settings.seatSpacing} min={20} max={200} suffix="px" onCommit={v => onSettings({ seatSpacing: v })} />
                </Field>
                <Field label="Row spacing">
                    <NumberInput value={settings.rowSpacing} min={20} max={200} suffix="px" onCommit={v => onSettings({ rowSpacing: v })} />
                </Field>
                <Field label="Row labels">
                    <SelectInput value={settings.rowScheme} options={LABEL_SCHEMES} onChange={v => onSettings({ rowScheme: v })} />
                </Field>
                <Field label="Seat labels">
                    <SelectInput value={settings.seatScheme} options={LABEL_SCHEMES} onChange={v => onSettings({ seatScheme: v })} />
                </Field>
            </Section>

            <Section title="View">
                <Toggle label="Show grid" checked={settings.showGrid} onChange={v => onSettings({ showGrid: v })} />
                <Toggle label="Snap to grid" checked={settings.snap} onChange={v => onSettings({ snap: v })} />
                <Toggle label="Seat numbers" checked={settings.showSeatNumbers} onChange={v => onSettings({ showSeatNumbers: v })} />
                <Field label="Grid size">
                    <NumberInput value={settings.gridSize} min={1} max={200} suffix="px" onCommit={v => onSettings({ gridSize: v })} />
                </Field>
            </Section>
        </>
    );
};

const ShapeInspector: React.FC<{ plan: Plan; shapeKey: string; commit: Props['commit'] }> = ({ plan, shapeKey: key, commit }) => {
    const shape = getShape(plan, key);
    if (!shape) return null;
    const isStage = key.startsWith('stage:');
    const stage = shape as Stage;
    const variant = isStage ? stage.variant : undefined;
    const isText = variant === 'text';
    const isLine = variant === 'line';
    const isGa = variant === 'ga';
    const set = (patch: Partial<Shape>, merge?: string) => commit(p => updateShape(p, key, patch), merge ? `${key}-${merge}` : undefined);
    const title = isText ? 'Text' : isLine ? 'Line' : isGa ? 'General admission' : variant === 'object' ? 'Object' : isStage ? (shape.type === 'polygon' ? 'Area' : 'Stage') : 'Entrance';
    return (
        <Section title={title}>
            {!isLine && (
                <Field label={isText ? 'Text' : 'Label'}>
                    <TextInput value={shape.name} onCommit={v => set({ name: v })} />
                </Field>
            )}
            {isText && (
                <Field label="Size">
                    <NumberInput value={stage.fontSize ?? 14} min={8} max={96} suffix="px" onCommit={fontSize => set({ fontSize } as Partial<Stage>)} />
                </Field>
            )}
            {isGa && (
                <>
                    <Field label="Type">
                        <Choice active={stage.gaType !== 'seated'} title="Standing" onClick={() => set({ gaType: 'standing' } as Partial<Stage>)}><Footprints className="h-4 w-4" /></Choice>
                        <Choice active={stage.gaType === 'seated'} title="Seated" onClick={() => set({ gaType: 'seated' } as Partial<Stage>)}><Armchair className="h-4 w-4" /></Choice>
                    </Field>
                    <Field label="Capacity">
                        <NumberInput value={stage.capacity ?? 0} min={1} max={100000} onCommit={capacity => set({ capacity: Math.round(capacity) } as Partial<Stage>)} />
                    </Field>
                    <Toggle label="Show icon" checked={stage.showIcon !== false} onChange={showIcon => set({ showIcon } as Partial<Stage>)} />
                </>
            )}
            {isStage && !isText && !isLine && shape.type !== 'polygon' && (
                <Field label="Shape">
                    <SelectInput
                        value={shape.type}
                        options={[{ value: 'square', label: 'Rectangle' }, { value: 'circle', label: 'Ellipse' }]}
                        onChange={v => set({ type: v } as Partial<Stage>)}
                    />
                </Field>
            )}
            {isStage && !isText && !isLine && !isGa && (
                <>
                    <Toggle label="Object icon" checked={!!stage.icon && stage.showIcon !== false} onChange={on => set({ showIcon: on, icon: on ? stage.icon || 'stage' : stage.icon } as Partial<Stage>)} />
                    {!!stage.icon && stage.showIcon !== false && <IconPicker value={stage.icon} onChange={icon => set({ icon, showIcon: true } as Partial<Stage>)} />}
                </>
            )}
            <div className="grid grid-cols-2 gap-1.5">
                <Field label="X"><NumberInput value={shape.position.x} onCommit={x => set({ position: { ...shape.position, x } })} /></Field>
                <Field label="Y"><NumberInput value={shape.position.y} onCommit={y => set({ position: { ...shape.position, y } })} /></Field>
                <Field label="W"><NumberInput value={shape.size.width} min={10} onCommit={width => set({ size: { ...shape.size, width } })} /></Field>
                {!isLine && <Field label="H"><NumberInput value={shape.size.height} min={10} onCommit={height => set({ size: { ...shape.size, height } })} /></Field>}
            </div>
            <SliderField
                label="Rotate"
                value={normalizeAngle(shape.rotation ?? 0)}
                min={-180}
                max={180}
                onChange={(v, live) => set({ rotation: ((v % 360) + 360) % 360 }, live ? 'rotate' : undefined)}
            />
            {isStage && !isText && !isLine && shape.type === 'square' && (
                <Field label="Corner radius">
                    <NumberInput value={stage.curveRadius ?? 0} min={0} onCommit={curveRadius => set({ curveRadius } as Partial<Stage>)} />
                </Field>
            )}
            {!isText && (
                <Field label={isLine ? 'Stroke' : 'Fill'}>
                    {!isLine && <ColorInput value={shape.color ?? '#ffffff'} onChange={color => set({ color }, 'fill')} />}
                    {!isLine && <span className="text-[10px] text-gray-400 ml-2">Stroke</span>}
                    <ColorInput value={shape.strokeColor ?? '#000000'} onChange={strokeColor => set({ strokeColor }, 'stroke')} />
                    <NumberInput value={shape.strokeWidth ?? 2} min={0} max={20} onCommit={strokeWidth => set({ strokeWidth })} />
                </Field>
            )}
        </Section>
    );
};

const keepWith = (before: Plan, after: Plan, keysOf: (p: Plan) => string[], shapes: Set<string> = new Set()): Selection => ({
    seats: new Set([...keysOf(before), ...keysOf(after)]),
    shapes,
});

const SectionInspector: React.FC<{ plan: Plan; zi: number; commit: Props['commit']; onKeepSelection: Props['onKeepSelection'] }> = ({ plan, zi, commit, onKeepSelection }) => {
    const [tab, setTab] = useState<'layout' | 'label'>('layout');
    const zone = plan.zones[zi];
    if (!zone?.section) return null;
    const layout = zone.section;
    const labels = { ...DEFAULT_SECTION_LABELS, ...zone.labels };
    const total = zone.rows.reduce((n, r) => n + r.seats.length, 0);
    const set = (patch: SectionPatch, merge?: string) => {
        const next = regenSection(plan, zi, patch);
        if (next === plan) return;
        commit(() => next, merge ? `section-${zi}-${merge}` : undefined);
        onKeepSelection(keepWith(plan, next, p => (p.zones[zi] ? zoneSeatKeys(p.zones[zi]) : [])));
    };
    const setLabels = (patch: Partial<typeof labels>) => set({ labels: patch });
    const rename = (v: string) => {
        const name = v.trim();
        if (!name || name === zone.name) return;
        commit(p => ({ ...p, zones: p.zones.map((z, i) => (i === zi ? { ...z, name, code: name } : z)) }));
    };

    return (
        <section className="border-b border-gray-200 px-4 py-3 dark:border-nightBorder">
            <div className="flex justify-center pb-2">
                <div className="w-28"><TextInput value={zone.name} onCommit={rename} /></div>
            </div>
            <div className="mb-3 grid grid-cols-2 overflow-hidden rounded border border-gray-200 text-[11px] font-medium uppercase tracking-wide dark:border-nightBorder">
                {(['layout', 'label'] as const).map(t => (
                    <button
                        key={t}
                        type="button"
                        onClick={() => setTab(t)}
                        className={cx(
                            'h-7 transition-colors',
                            tab === t
                                ? 'bg-primary99 text-primary30 shadow-[inset_0_-2px_0] shadow-primary40 dark:bg-nightAccent/15 dark:text-nightAccent dark:shadow-nightAccent'
                                : 'text-gray-500 hover:bg-gray-50 dark:text-nightMuted dark:hover:bg-nightElevated',
                        )}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {tab === 'layout' ? (
                <div className="space-y-2">
                    <Field label="Row count">
                        <NumberInput value={layout.rows} min={1} max={200} onCommit={rows => set({ layout: { rows } })} />
                    </Field>
                    <Field label="Seats per row">
                        <NumberInput value={layout.seatsPerRow} min={1} max={300} onCommit={seatsPerRow => set({ layout: { seatsPerRow } })} />
                    </Field>
                    <Field label="Total">
                        <span className="text-xs font-semibold tabular-nums text-gray-900 dark:text-nightTextSecondary">{total}</span>
                    </Field>
                    <Field label="Alignment" hint="How rows with fewer seats line up">
                        {([['left', TextAlignStart], ['center', TextAlignCenter], ['right', TextAlignEnd]] as [SectionAlign, typeof TextAlignStart][]).map(([align, Icon]) => (
                            <Choice key={align} active={layout.align === align} title={`Align ${align}`} onClick={() => set({ layout: { align } })}>
                                <Icon className="h-4 w-4" />
                            </Choice>
                        ))}
                    </Field>
                    <SliderField label="Curve" value={layout.curve} min={-180} max={180} hint="Bend the rows into an arc" onChange={(curve, live) => set({ layout: { curve } }, live ? 'curve' : undefined)} />
                    <SliderField label="Skew" value={layout.skew} min={-45} max={45} hint="Shift each row sideways" onChange={(skew, live) => set({ layout: { skew } }, live ? 'skew' : undefined)} />
                    <SliderField label="Rotate" value={layout.rotation} min={-180} max={180} onChange={(rotation, live) => set({ layout: { rotation } }, live ? 'rotate' : undefined)} />
                    <Field label="Seat spacing">
                        <NumberInput value={layout.seatSpacing} min={15} max={200} suffix="px" onCommit={seatSpacing => set({ layout: { seatSpacing } })} />
                    </Field>
                    <Field label="Row spacing">
                        <NumberInput value={layout.rowSpacing} min={15} max={200} suffix="px" onCommit={rowSpacing => set({ layout: { rowSpacing } })} />
                    </Field>
                </div>
            ) : (
                <div className="space-y-2">
                    <p className="text-[11px] text-gray-400">Control what your attendees see when purchasing. Labels are always visible in the design tool.</p>
                    <Field label="Section prefix"><TextInput value={labels.sectionPrefix} onCommit={sectionPrefix => setLabels({ sectionPrefix })} /></Field>
                    <Field label="Row prefix"><TextInput value={labels.rowPrefix} onCommit={rowPrefix => setLabels({ rowPrefix })} /></Field>
                    <Field label="Seat prefix"><TextInput value={labels.seatPrefix} onCommit={seatPrefix => setLabels({ seatPrefix })} /></Field>
                    <Toggle label="Show section label" checked={labels.showSection} onChange={showSection => setLabels({ showSection })} />
                    <Field label="Show row label">
                        <SelectInput value={labels.showRows} options={ROW_LABEL_SIDES} onChange={showRows => setLabels({ showRows })} />
                    </Field>
                    <div className="my-1 border-t border-gray-100 dark:border-nightBorder" />
                    <Field label="Row">
                        <SelectInput value={labels.rowScheme} options={LABEL_SCHEMES} onChange={rowScheme => setLabels({ rowScheme, rowStart: defaultStartFor(rowScheme) })} />
                    </Field>
                    <Field label="Start with"><TextInput value={labels.rowStart} onCommit={rowStart => setLabels({ rowStart })} /></Field>
                    <Field label="Direction">
                        <Choice active={labels.rowReverse} title="Bottom to top" onClick={() => setLabels({ rowReverse: true })}><ArrowUp className="h-4 w-4" /></Choice>
                        <Choice active={!labels.rowReverse} title="Top to bottom" onClick={() => setLabels({ rowReverse: false })}><ArrowDown className="h-4 w-4" /></Choice>
                    </Field>
                    <div className="my-1 border-t border-gray-100 dark:border-nightBorder" />
                    <Field label="Seat">
                        <SelectInput value={labels.seatScheme} options={LABEL_SCHEMES} onChange={seatScheme => setLabels({ seatScheme, seatStart: defaultStartFor(seatScheme) })} />
                    </Field>
                    <Field label="Start with"><TextInput value={labels.seatStart} onCommit={seatStart => setLabels({ seatStart })} /></Field>
                    <Field label="Direction">
                        <Choice active={labels.seatReverse} title="Right to left" onClick={() => setLabels({ seatReverse: true })}><ArrowLeft className="h-4 w-4" /></Choice>
                        <Choice active={!labels.seatReverse} title="Left to right" onClick={() => setLabels({ seatReverse: false })}><ArrowRight className="h-4 w-4" /></Choice>
                    </Field>
                </div>
            )}
        </section>
    );
};

const SeatsInspector: React.FC<Props & { sectionMode?: boolean }> = ({ plan, index, selection, settings, commit, onSelect, onKeepSelection, onRotate, sectionMode, lockedMessage }) => {
    const info = useMemo(() => {
        const fullIds = fullRowIds(plan, index, selection.seats);
        const rowIds = new Set<string>();
        const cats = new Set<string>();
        selection.seats.forEach(k => {
            const r = index.seatRow.get(k);
            if (r) rowIds.add(r);
            const ref = index.seats.get(k);
            if (ref) cats.add(plan.zones[ref.zi].rows[ref.ri].seats[ref.si].category ?? '');
        });
        const rows = [...fullIds].map(id => {
            const ref = index.rows.get(id)!;
            const zone = plan.zones[ref.zi];
            const row = zone.rows[ref.ri];
            const pts = row.seats.map(s => seatAbs(zone, row, s));
            const c = pts.reduce((a, p) => ({ x: a.x + p.x / pts.length, y: a.y + p.y / pts.length }), { x: 0, y: 0 });
            return { id, row, zone, pts, c };
        }).sort((a, b) => a.c.y - b.c.y || a.c.x - b.c.x);
        let table: { rowId: string; shape: Stage; count: number } | null = null;
        if (selection.shapes.size === 1 && fullIds.size === 1 && rowIds.size === 1) {
            const only = rows[0];
            const shape = only.row.tableId ? plan.stages?.find(st => st.id === only.row.tableId) : undefined;
            if (shape && selection.shapes.has(`stage:${shape.id}`)) table = { rowId: only.id, shape, count: only.row.seats.length };
        }
        let accessible = true;
        selection.seats.forEach(k => {
            const ref = index.seats.get(k);
            if (ref && !plan.zones[ref.zi].rows[ref.ri].seats[ref.si].accessible) accessible = false;
        });
        const zones = new Set<number>();
        selection.seats.forEach(k => {
            const ref = index.seats.get(k);
            if (ref) zones.add(ref.zi);
        });
        const inSection = zones.size > 0 && [...zones].every(zi => !!plan.zones[zi].section);
        return {
            fullIds,
            rowIds,
            rows,
            table,
            accessible,
            inSection,
            sectionZone: inSection && zones.size === 1 ? [...zones][0] : -1,
            allFull: fullIds.size === rowIds.size,
            category: cats.size === 1 ? [...cats][0] : null,
        };
    }, [plan, index, selection]);
    const gap = useMemo(
        () => (info.allFull && info.rows.length > 1 ? rowGapInfo(plan, index, info.rows.map(r => r.id), settings.seatSpacing) : null),
        [info, plan, index, settings.seatSpacing],
    );

    const first = info.rows[0];
    const geo = first ? measureRow(first.pts, settings.seatSpacing) : null;
    const defaults = { spacing: settings.seatSpacing, seatScheme: settings.seatScheme };
    const reshapeAll = (edit: Parameters<typeof reshapeRow>[2], merge?: string) =>
        commit(p => mapRows(p, (row, zone) => (info.fullIds.has(row.uuid!) ? reshapeRow(row, zone, edit, defaults) : row)), merge);

    const singleSeat = selection.seats.size === 1 ? index.seats.get([...selection.seats][0]) : undefined;
    const seat = singleSeat ? plan.zones[singleSeat.zi].rows[singleSeat.ri].seats[singleSeat.si] : undefined;
    const seatRow = singleSeat ? plan.zones[singleSeat.zi].rows[singleSeat.ri] : undefined;

    return (
        <>
            {info.table && (() => {
                const t = info.table;
                const shape = tableShapeOf(t.shape);
                const apply = (opts: Partial<Parameters<typeof reshapeTable>[3]>) => {
                    const next = reshapeTable(plan, t.rowId, t.shape.id, { spacing: settings.seatSpacing, seatScheme: 'numeric', ...opts });
                    if (next === plan) return;
                    commit(() => next);
                    const rowKeys = (p: Plan) => p.zones.flatMap(z => z.rows).find(r => r.uuid === t.rowId)?.seats.map(seatKey) ?? [];
                    onKeepSelection(keepWith(plan, next, rowKeys, selection.shapes));
                };
                return (
                    <Section title="Table">
                        <Field label="Label">
                            <TextInput
                                value={t.shape.name}
                                onCommit={v => commit(p => updateShape(updateRow(p, t.rowId, r => ({ ...r, row_number: v })), `stage:${t.shape.id}`, { name: v }))}
                            />
                        </Field>
                        <Field label="Shape">
                            <SelectInput<TableShape> value={shape} options={TABLE_SHAPES} onChange={v => apply({ shape: v })} />
                        </Field>
                        <Field label="Seats">
                            <NumberInput value={t.count} min={1} max={60} onCommit={count => apply({ count })} />
                        </Field>
                        {shape === 'rect' && (
                            <Field label="End seats" hint="Seats at the two short ends of the table, included in the seat count">
                                <NumberInput value={t.shape.endSeats ?? 0} min={0} max={t.count} onCommit={endSeats => apply({ endSeats: Math.round(endSeats) })} />
                            </Field>
                        )}
                        <SliderField
                            label="Rotate"
                            value={normalizeAngle(t.shape.rotation ?? 0)}
                            min={-180}
                            max={180}
                            onChange={(v, live) => onRotate(v - normalizeAngle(t.shape.rotation ?? 0), live ? 'rotate-table' : undefined)}
                        />
                        <Field label="Size (W x H)">
                            <NumberInput value={t.shape.size.width} min={10} onCommit={width => apply({ size: { ...t.shape.size, width } })} />
                            <span className="text-[10px] text-gray-400">x</span>
                            <NumberInput value={t.shape.size.height} min={10} onCommit={height => apply({ size: { ...t.shape.size, height } })} />
                        </Field>
                        <p className="text-[11px] text-gray-400">Drag a corner handle to resize. Hold Shift to keep proportions.</p>
                    </Section>
                );
            })()}

            <Section title="Tier">
                <CategoryPicker disabled={!!lockedMessage} plan={plan} current={info.category} onPick={name => commit(p => setSeatCategory(p, selection.seats, name))} />
                <div className="pt-1">
                    <Toggle label="Wheelchair accessible" checked={info.accessible} onChange={v => commit(p => setAccessible(p, selection.seats, v))} />
                </div>
            </Section>

            {info.inSection && !sectionMode && (() => {
                const zone = plan.zones[info.sectionZone];
                const selectSection = zone && (
                    <Button onClick={() => onSelect({ seats: new Set(zoneSeatKeys(zone)), shapes: new Set() })}>Select section {zone.name}</Button>
                );
                if (info.allFull && info.rows.length === 1 && zone) {
                    const ri = index.rows.get(first.id)!.ri;
                    return (
                        <Section title={`Row ${first.row.row_number}`}>
                            <Field label="Seats">
                                <NumberInput
                                    value={first.row.seats.length}
                                    min={1}
                                    max={300}
                                    onCommit={n => {
                                        const next = regenSection(plan, info.sectionZone, { counts: { [ri]: Math.round(n) } });
                                        if (next === plan) return;
                                        commit(() => next);
                                        const rowKeys = (p: Plan) => p.zones[info.sectionZone]?.rows[ri]?.seats.map(seatKey) ?? [];
                                        onKeepSelection(keepWith(plan, next, rowKeys));
                                    }}
                                />
                            </Field>
                            <p className="text-[11px] text-gray-400">Rows, curve and labels of this row come from its section.</p>
                            {selectSection}
                        </Section>
                    );
                }
                return zone ? <Section title="Section">{selectSection}</Section> : null;
            })()}

            {seat && seatRow && !info.allFull && !info.inSection && (
                <Section title="Seat">
                    <Field label="Row">
                        <span className="text-xs text-gray-900 dark:text-nightTextSecondary">{seatRow.row_number || '(no label)'}</span>
                    </Field>
                    <Field label="Seat number">
                        <TextInput value={seat.seat_number} onCommit={v => commit(p => updateSeat(p, seatKey(seat), { seat_number: v }))} />
                    </Field>
                </Section>
            )}

            {info.allFull && geo && !info.table && !info.inSection && (
                <Section title={info.rows.length === 1 ? 'Row' : `${info.rows.length} rows`}>
                    {info.rows.length === 1 && (
                        <>
                            <Field label="Label">
                                <TextInput value={first.row.row_number} onCommit={v => commit(p => updateRow(p, first.id, r => ({ ...r, row_number: v })))} />
                            </Field>
                            <Field label="Seats">
                                <NumberInput value={geo.count} min={1} max={500} onCommit={count => reshapeAll({ count, keepFirst: true })} />
                            </Field>
                            <Field label="Angle">
                                <NumberInput value={Math.round(geo.angle * 10) / 10} suffix="deg" onCommit={angle => reshapeAll({ angle })} />
                            </Field>
                        </>
                    )}
                    <Field label="Seat spacing" hint="Distance between seat centers">
                        <NumberInput value={Math.round(geo.spacing * 10) / 10} min={20} max={200} suffix="px" onCommit={spacing => reshapeAll({ spacing, keepFirst: true })} />
                    </Field>
                    <Field label="Curve" hint="Arc sweep in degrees. 0 is straight, 180 is a half circle">
                        <input
                            type="range"
                            min={-180}
                            max={180}
                            step={1}
                            value={Math.round(geo.sweep)}
                            onChange={e => reshapeAll({ sweep: Number(e.target.value) }, 'curve')}
                            className="flex-1 min-w-0 accent-primary40 dark:accent-nightAccent"
                        />
                        <div className="w-14 shrink-0">
                            <NumberInput value={Math.round(geo.sweep)} min={-350} max={350} onCommit={sweep => reshapeAll({ sweep })} />
                        </div>
                    </Field>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                        <Button onClick={() => reshapeAll({ sweep: 0 })} title="Remove curve">
                            <Spline className="h-3.5 w-3.5" /> Straighten
                        </Button>
                        <Button onClick={() => commit(p => reverseSeatOrder(p, info.fullIds))} title="Reverse seat numbering direction">
                            <ArrowLeftRight className="h-3.5 w-3.5" /> Flip numbering
                        </Button>
                    </div>
                </Section>
            )}

            {gap && !info.table && !info.inSection && (
                <Section title="Arrange">
                    <div className="flex items-center gap-1">
                        <span className="w-20 shrink-0 text-xs text-gray-600 dark:text-nightTextSecondary">Align</span>
                        {([['left', AlignStartVertical], ['center', AlignCenterVertical], ['right', AlignEndVertical]] as const).map(([mode, Icon]) => (
                            <Button key={mode} title={`Align ${mode}`} onClick={() => commit(p => alignRows(p, index, info.rows.map(r => r.id), mode))}>
                                <Icon className="h-3.5 w-3.5" />
                            </Button>
                        ))}
                    </div>
                    <Field label="Row gap" hint="Distance between consecutive rows, measured perpendicular to the first row">
                        <NumberInput
                            value={Math.round(gap.gap * 10) / 10}
                            min={0}
                            max={500}
                            suffix="px"
                            onCommit={v => commit(p => spaceRows(p, index, info.rows.map(r => r.id), v, settings.seatSpacing))}
                        />
                    </Field>
                </Section>
            )}

            {info.allFull && !info.inSection && (
                <Section title="Numbering">
                    {!info.table && <NumberingTool
                        title={info.rows.length === 1 ? 'Row label' : 'Row labels (top to bottom)'}
                        defaultScheme={settings.rowScheme}
                        directions={['Top to bottom', 'Bottom to top']}
                        onApply={(start, scheme, reverse) => {
                            const ids = info.rows.map(r => r.id);
                            commit(p => renumberRows(p, reverse ? ids.reverse() : ids, start, scheme));
                        }}
                    />}
                    <NumberingTool
                        title="Seat numbers"
                        defaultScheme={settings.seatScheme}
                        directions={['First to last', 'Last to first']}
                        onApply={(start, scheme, reverse) => commit(p => renumberSeats(p, info.fullIds, start, scheme, reverse))}
                    />
                </Section>
            )}

            {!info.allFull && !info.inSection && (
                <Section title="Selection">
                    <Button
                        onClick={() => {
                            const seats = new Set<string>();
                            info.rowIds.forEach(id => {
                                const ref = index.rows.get(id)!;
                                plan.zones[ref.zi].rows[ref.ri].seats.forEach(s => seats.add(seatKey(s)));
                            });
                            onSelect({ seats, shapes: selection.shapes });
                        }}
                    >
                        Select entire rows
                    </Button>
                </Section>
            )}
        </>
    );
};

const Inspector: React.FC<Props> = props => {
    const { selection, onDelete, onDuplicate, onRotate, plan } = props;
    const nSeats = selection.seats.size;
    const nShapes = selection.shapes.size;
    const empty = !nSeats && !nShapes;
    const sectionZi = useMemo(() => {
        if (nShapes || !nSeats) return -1;
        const full = fullZoneIndexes(plan, selection.seats);
        if (full.size !== 1) return -1;
        const zi = [...full][0];
        return plan.zones[zi].section && zoneSeatKeys(plan.zones[zi]).length === nSeats ? zi : -1;
    }, [plan, selection, nSeats, nShapes]);

    const summary = [
        nSeats && `${nSeats} seat${nSeats === 1 ? '' : 's'}`,
        nShapes && `${nShapes} object${nShapes === 1 ? '' : 's'}`,
    ].filter(Boolean).join(', ');

    return (
        <aside className={cx('shrink-0 flex flex-col bg-white dark:bg-nightSurface', props.className ?? 'w-72 border-l border-gray-200 dark:border-nightBorder')}>
            <div className="flex items-center justify-between px-4 h-10 border-b border-gray-200 dark:border-nightBorder">
                <span className="text-xs font-semibold text-gray-900 dark:text-nightTextSecondary">{empty ? 'Properties' : summary}</span>
                {!empty && (
                    <div className="flex items-center gap-0.5">
                        <button type="button" title="Rotate -15 deg" onClick={() => onRotate(-15)} className="h-6 w-6 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 dark:text-nightMuted dark:hover:bg-nightElevated">
                            <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" title="Rotate +15 deg" onClick={() => onRotate(15)} className="h-6 w-6 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 dark:text-nightMuted dark:hover:bg-nightElevated">
                            <RotateCw className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" title="Duplicate (Ctrl D)" onClick={onDuplicate} className="h-6 w-6 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 dark:text-nightMuted dark:hover:bg-nightElevated">
                            <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" title="Delete (Del)" onClick={onDelete} className="h-6 w-6 flex items-center justify-center rounded text-gray-500 hover:bg-error95 hover:text-error40 dark:text-nightMuted dark:hover:bg-error10">
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}
            </div>
            <div className="flex-1 overflow-y-auto">
                {props.tool === 'table' && (
                    <Section title="New table">
                        <Field label="Seats">
                            <NumberInput value={props.settings.tableSeats} min={1} max={60} onCommit={v => props.onSettings({ tableSeats: Math.round(v) })} />
                        </Field>
                        <Field label="Shape">
                            <SelectInput<TableShape>
                                value={props.settings.tableShape}
                                options={TABLE_SHAPES}
                                onChange={v => props.onSettings({ tableShape: v })}
                            />
                        </Field>
                        {props.settings.tableShape === 'rect' && (
                            <Field label="End seats">
                                <NumberInput value={props.settings.tableEndSeats} min={0} max={props.settings.tableSeats} onCommit={v => props.onSettings({ tableEndSeats: Math.round(v) })} />
                            </Field>
                        )}
                    </Section>
                )}
                {empty && <AddPanel plan={plan} onCreate={props.onCreate} />}
                {empty && <PlanInspector {...props} />}
                {sectionZi >= 0 && <SectionInspector plan={plan} zi={sectionZi} commit={props.commit} onKeepSelection={props.onKeepSelection} />}
                {nSeats > 0 && <SeatsInspector {...props} sectionMode={sectionZi >= 0} />}
                {nShapes === 1 && !nSeats && <ShapeInspector plan={plan} shapeKey={[...selection.shapes][0]} commit={props.commit} />}
            </div>
        </aside>
    );
};

export default Inspector;
